import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqItems = [
  {
    question: 'Does Sandwell\'s GREEN overall DfT rating block my pothole claim?',
    answer: 'No. GREEN overall means Sandwell performs above average on network-level spend and asset management — but the DfT Condition scorecard is AMBER, and the council\'s own data shows unclassified roads in RED condition rose from 15% to 23% between 2019/20 and 2024/25. Section 58 of the Highways Act 1980 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on the headline scorecard.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'U-roads make up 654.3km — 74.7% of Sandwell\'s 876.4km carriageway network. At the 2024/25 survey, 23% were in RED condition — roughly 150km of estate streets, village routes and residential roads. Condition data is collected via Course Visual Inspections over a four-year rolling cycle and then combined. If your incident occurred on a stretch not yet captured in the current four-year window, the council\'s network-level knowledge of that road may be stale.',
  },
  {
    question: 'Sandwell only fixed 1,287 potholes in 2024/25 — does that mean the roads are fine?',
    answer: 'Not necessarily. The council itself states that because roughly 90% of highway spend goes on preventative maintenance, it "repairs relatively few potholes" — 1,287 in 2024/25, down from 1,812 in 2023/24. Yet U-road RED share climbed every year for five years to 23%. Low reactive pothole counts and rising RED-condition estate roads can coexist: prevention may be holding classified roads steady while 654km of residential network still deteriorates.',
  },
  {
    question: 'Why is the DfT Condition scorecard AMBER when A and B/C roads look stable?',
    answer: 'Sandwell\'s own SCANNER data shows A-roads at 2.4% RED and B/C roads at 2.18% RED in 2024/25 — broadly flat for five years. But those classified roads are only 222.1km, under a quarter of the network. The DfT\'s AMBER Condition rating reflects the deteriorating trend on unclassified roads — 15% RED in 2019/20 rising to 23% in 2024/25 — which is where most Sandwell motorists actually drive.',
  },
  {
    question: 'How does Sandwell\'s four-year U-road survey cycle affect Section 58?',
    answer: 'Sandwell collects U-road condition via Course Visual Inspections over a four-year period, then combines the data into annual RED percentages. SCANNER surveys on A and B/C roads cover 50% of the network each year. To rely on Section 58, the council must show a reasonable system for knowing road condition. A four-year rolling window on 654km of residential roads — where RED share has risen every year — leaves questions about how quickly deterioration on your specific street was identified.',
  },
  {
    question: 'Sandwell spends 91.9% on preventative maintenance — can I still claim?',
    answer: 'Yes. Projected 2025/26 capital spend is £10.94m against a £8.44m DfT allocation, with 91.9% classed as preventative — a GREEN Spend scorecard. But aggregate investment does not prove the individual pothole was known and repaired within reasonable timescales. Prior reports through MySandwell or FixMyStreet, photos showing defect age, and the road\'s place in the four-year CVI survey cycle matter more than the budget headline.',
  },
  {
    question: 'What does the PAS 2161 methodology change in 2026/27 mean for my claim?',
    answer: 'From 2026/27, local authorities must use PAS 2161-accredited suppliers, categorising roads into five condition bands instead of three. Sandwell\'s transparency report notes this shift explicitly. Condition records before and after that change may not be directly comparable — which makes capturing evidence of your specific defect now, under the current RED/amber/green framework, more important.',
  },
];

export function SandwellCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Sandwell Pothole Claims | 23% of Estate Roads in RED | Fixtyer"
        description="Sandwell Council earns GREEN DfT scorecards for spend and best practice, yet Condition is AMBER and 23% of its 654km of unclassified roads are in RED condition — up from 15% in five years. See what this means for your pothole claim."
        canonical="/councils/sandwell"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Sandwell', url: '/councils/sandwell' },
        ]}
      />
      <FAQSchema faqs={faqItems} />
      <ServiceSchema
        councilName="Sandwell Metropolitan Borough Council"
        councilSlug="sandwell"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/sandwell"
        headline="Sandwell Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Sandwell</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Nearly a Quarter of Sandwell's Estate Roads Are Now RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            DfT rates Sandwell <strong className="text-emerald-400">GREEN</strong> overall — spend and best practice
            both above par. Yet Condition is <strong className="text-amber-400">AMBER</strong>, because the council's
            own data shows <strong className="text-rose-400">23% of its 654km of unclassified roads</strong> now need
            maintenance, up from 15% in 2019/20. Classified A and B/C roads look stable. Three-quarters of the network
            does not.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">23%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024/25)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up every year for five years — from 15% in 2019/20. On 654.3km of residential and estate roads,
              that is roughly 150km the council itself classifies as needing maintenance.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Overall, AMBER Condition — the Split Scorecard
          </h2>
          <p className="text-surface-600 mb-8">
            Three GREEN scorecards and one AMBER — what the DfT sees versus what estate-road data shows
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Sandwell Council DfT Road Maintenance Ratings 2025-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">What GREEN Means Here</h3>
              <p className="text-emerald-800 text-sm">
                Sandwell projects £10.94m capital spend against an £8.44m DfT allocation in 2025/26, classifies
                91.9% of maintenance as preventative, and maintains a Highway Infrastructure Asset Management Plan
                approved by Cabinet in June 2025. A-roads have held at 2.4% RED for five years. The council chairs
                the West Midlands Joint Authorities Group on streetworks and participates in the DfT-funded Live Labs 2
                decarbonisation programme. This is a well-run authority by national standards.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why Condition Is AMBER</h3>
              <p className="text-amber-800 text-sm">
                The DfT Condition scorecard is not fooled by stable classified-road averages. Sandwell's own Table 6
                shows U-road RED share rising every year — 15%, 17%, 18%, 19%, 22%, 23% — while the council
                acknowledges "a slight deteriorating trend in recent years" on the 654km unclassified network that
                carries most local traffic through Oldbury, Smethwick, West Bromwich, Wednesbury, Tipton and Rowley Regis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            876km — and £3.9 Billion of Public Asset
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Sandwell's June 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">876.4km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">654.3km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">74.7% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">127.3km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">94.8km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
          </div>

          <p className="text-surface-600 text-sm mb-6">
            The network also includes 1,480.2km of footways, 10.29km of cycleways, 106km of public rights of way,
            41,857 highway drainage gullies, and 117 bridges — with no structures in very poor or red risk condition
            per the council's 2025/26 programme.
          </p>

          <QuoteCard
            quote="The Sandwell highway network is over 876 km in length and is divided into various asset types for the purpose of maintenance. The whole network has an estimated value of £3.9 billion, which makes it the largest and most visible asset Sandwell Council is responsible for."
            source="Sandwell MBC Local Highways Maintenance Transparency Report — Annex A (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Sandwell's own transparency report — classified roads flat, estate roads slipping
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (127.3km — 14.5% of network): stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.7%</td>
                    <td className="px-4 py-2 text-center">19.9%</td>
                    <td className="px-4 py-2 text-center">77.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.5%</td>
                    <td className="px-4 py-2 text-center">19.9%</td>
                    <td className="px-4 py-2 text-center">77.6%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">2.4%</td>
                    <td className="px-4 py-2 text-center font-bold">20.0%</td>
                    <td className="px-4 py-2 text-center font-bold">77.6%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              SCANNER surveys cover 50% of A-roads each year; figures combine two years of data. Condition has
              remained broadly stable — credit where due on principal routes.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (94.8km — 10.8% of network): stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">2.22%</td>
                    <td className="px-4 py-2 text-center">19.64%</td>
                    <td className="px-4 py-2 text-center">78.14%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">2.62%</td>
                    <td className="px-4 py-2 text-center">21.00%</td>
                    <td className="px-4 py-2 text-center">76.39%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">2.18%</td>
                    <td className="px-4 py-2 text-center font-bold">20.87%</td>
                    <td className="px-4 py-2 text-center font-bold">76.95%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              The council's own report states B and C roads "have also remained broadly stable over the last five years
              in good overall condition." But they are barely one-ninth of the network.
            </p>
          </div>

          {/* U-roads — the story */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (654.3km — 74.7% of network): deteriorating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2019/20</td>
                    <td className="px-4 py-2 text-center">15%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">17%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">18%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">19%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">22%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">23%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED share has risen every single year for five years — an eight-point climb. At 23% on 654.3km,
              roughly 150km of residential and estate roads are in the category the council defines as "should be
              considered for maintenance."
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The Prevention-Condition Paradox</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">91.9%</div>
                <div className="text-brand-300 text-sm">Preventative maintenance share (2025/26 projected)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">1,287</div>
                <div className="text-brand-300 text-sm">Potholes filled in 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">23%</div>
                <div className="text-brand-300 text-sm">U-roads in RED condition (2024/25)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Sandwell spends roughly <span className="text-emerald-400">nine-tenths of its budget on prevention</span> and
              repairs relatively few potholes — yet estate-road RED condition keeps climbing. The AMBER Condition scorecard
              is not about how much the council spends. It is about what is happening on the roads most people use.
            </p>
          </div>
        </div>
      </section>

      {/* The 654km survey gap */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 654km Survey Window
          </h2>
          <p className="text-surface-600 mb-8">
            Course Visual Inspections on a four-year rolling cycle — different rulers for different road classes
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">How U-Roads Are Measured</h3>
              <p className="text-surface-600 text-sm mb-3">
                Sandwell collects U-road condition via Course Visual Inspections (CVI) over a four-year period,
                then combines the data to produce annual RED percentages. A and B/C roads use SCANNER laser surveys
                covering 50% of the network each year.
              </p>
              <p className="text-surface-600 text-sm">
                The council defines RED as roads that <strong>"should be considered for maintenance."</strong> When
                23% of 654km sits in that category, the question for your claim is not the borough-wide average —
                it is whether your specific road was identified and treated in time.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Deterioration Admission</h3>
              <QuoteCard
                quote="The condition of the unclassified carriageway network in Sandwell has shown a slight deteriorating trend in recent years, although Sandwell has secured additional funding and have plans in place that will address this and maintain the condition of the unclassified carriageway network in a satisfactory state."
                source="Sandwell MBC Local Highways Maintenance Transparency Report — Annex A (June 2025)"
              />
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Sandwell's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last included in the four-year CVI survey cycle?</li>
              <li>• If 23% of U-roads were RED at the latest combined survey, what was done about yours?</li>
              <li>• Does a well-funded preventative programme on A and B/C roads extend to your residential street?</li>
              <li>• Can the council point to contemporaneous condition data for the road type where your incident occurred?</li>
            </ul>
            <p className="text-white font-medium">
              A GREEN overall rating measures borough-wide performance. Section 58 turns on the specific defect
              that damaged your vehicle — and 74.7% of Sandwell's network is U-roads on a different survey regime.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            7,870 Potholes in Five Years — Fewer Than You Might Expect
          </h2>
          <p className="text-surface-600 mb-8">
            Sandwell's own reactive repair figures — and what the council says they mean
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive carriageway potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,619</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,739</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,413</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,812</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">1,287</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-brand-900">7,870</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mb-8">
            <QuoteCard
              quote="Typically, 90% highway maintenance spend in Sandwell goes on preventing defects becoming serious issues. It also ensures that, given the length of the road network in Sandwell, we repair relatively few potholes."
              source="Sandwell MBC Local Highways Maintenance Transparency Report — Annex A (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~4 Potholes a Day, Not 76</h3>
              <p className="text-surface-600 text-sm">
                Sandwell filled 1,287 potholes in 2024/25 — roughly 3.5 per day across 876km. That is far below
                councils reporting tens of thousands of annual repairs. The council attributes this to preventative
                surfacing: 14.1km resurfaced and 35.2km surface-treated in 2024/25. But U-road RED share still
                rose to 23%, which suggests prevention is not reaching the whole residential network at the same pace.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">When Low Counts Cut Both Ways</h3>
              <p className="text-amber-800 text-sm">
                A low pothole count strengthens Sandwell's Section 58 paperwork on classified roads — evidence of
                effective prevention. On a U-road where 23% of comparable streets are already RED, it raises a
                different question: if so few potholes are being filled reactively, how are deteriorating estate
                roads being brought back into maintenance before defects form?
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending and programmes */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Following The Money
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of highway maintenance spending from Sandwell's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Sandwell highway maintenance spending by year</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Additional council capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                <tr>
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£5.96m</td>
                  <td className="px-4 py-3 text-center">£4.89m</td>
                  <td className="px-4 py-3 text-center">£10.85m</td>
                  <td className="px-4 py-3 text-center">£5.70m</td>
                  <td className="px-4 py-3 text-center">91.0%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£5.25m</td>
                  <td className="px-4 py-3 text-center">£2.69m</td>
                  <td className="px-4 py-3 text-center">£7.94m</td>
                  <td className="px-4 py-3 text-center">£3.82m</td>
                  <td className="px-4 py-3 text-center">89.5%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">£8.44m</td>
                  <td className="px-4 py-3 text-center font-bold">£2.50m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£10.94m</td>
                  <td className="px-4 py-3 text-center">£3.90m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">91.9%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Preventative Programme</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 49 carriageway resurfacing schemes</li>
                <li>• 77 carriageway surface treatment schemes</li>
                <li>• 30 footway resurfacing schemes</li>
                <li>• 150 footway surface treatment schemes</li>
                <li>• £3.31m preventive footway maintenance programme</li>
                <li>• ~£1.5m preventative bridge maintenance programme</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2024/25 Treatment Delivered</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 14.1km carriageway resurfaced or reconstructed</li>
                <li>• 35.2km other carriageway surface treatments</li>
                <li>• Multi-Hog machine acquired in 2025 for faster surface preparation</li>
                <li>• Asphalt recycling trial to reuse material from worn-out roads</li>
                <li>• Live Labs 2 decarbonisation trials with Transport for West Midlands</li>
              </ul>
            </div>
          </div>

          <QuoteCard
            quote="Red – Should be considered for maintenance"
            source="Sandwell MBC Local Highways Maintenance Transparency Report — Annex A (June 2025)"
          />
        </div>
      </section>

      {/* Balanced claims assessment */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Rated, AMBER-Condition Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Sandwell is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN overall, spend and best-practice DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 91.9% preventative maintenance share projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A and B/C roads stable at ~2% RED for five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented HIAMP, lifecycle modelling and ISO-aligned asset management
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Relatively low reactive pothole count — evidence of active prevention on main roads
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on A and B/C roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER Condition — U-road RED share up from 15% to 23% in five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 654.3km of U-roads surveyed on a four-year CVI rolling cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits "slight deteriorating trend" on the estate-road network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 74.7% of the network is U-roads — where most pothole claims originate
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Low pothole-fill counts alongside rising RED percentages — prevention may not be reaching your street
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and best-practice scorecards, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (MySandwell, FixMyStreet) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the four-year CVI survey window and 23% RED share are your strongest structural arguments</li>
              <li>• Proximity to recent utility works or failed reinstatements on One.Network</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Sandwell's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Sandwell first
          </h2>
          <p className="text-surface-600 mb-6">
            Sandwell's transparency report describes reactive maintenance as roughly 10% of highway spend, with
            pothole repairs undertaken when preventative treatments cannot prevent surface failure. Reporting the
            defect through the council creates a dated record — useful evidence if the pothole was reported before
            your incident, or if the council failed to repair it within a reasonable time.
          </p>
          <a
            href="https://www.sandwell.gov.uk/roads-travel-parking/emergency-highways-problem"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Sandwell Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the online form for non-emergency potholes. For dangerous defects, call 0121 368 1177 (Mon–Fri 8am–5.30pm)
            or 07867 506556 out of hours. Keep your reference number.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Sandwell?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 23% RED trend cited</li>
                <li>• No four-year CVI survey-gap argument</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road RED rise from 15% to 23% documented</li>
                <li>✅ 654km estate-road network and CVI cycle argued</li>
                <li>✅ AMBER Condition vs GREEN overall tension cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Sandwell</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=sandwell"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Sandwell Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqItems.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.sandwell.gov.uk/downloads/download/629/asset-management-plan"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Sandwell MBC Local Highways Maintenance Transparency Report — Annex A (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
